import { useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Collapse, Grid, Link, Stack, Typography, useTheme } from '@mui/material'
import { ChevronDownIcon, ChevronUpIcon, LockIcon, SlidersHorizontalIcon, XIcon } from 'lucide-react'
import { Button, Card, RangeSlider, Skeleton } from '@repo/ui'
import {
  MASTER_FACETS,
  periodBoundForInput,
  periodBoundFromInput,
  SMALL_SAMPLE_N,
  VALUE_RANGE_STOPS,
  type MasterAttribution,
  type MasterFacet,
} from '@repo/types'

import { EmptyState, SelectField } from 'components'
import { SankeyChart } from 'components/analytics/SankeyChart'
import { AnalyticsBlock } from 'components/display/Analytics/Analytics'
import { KpiTileGrid } from 'components/display/Analytics/KpiTileGrid'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { ViewToggle, type ViewToggleOption } from 'components/input/ViewToggle/ViewToggle'
import { useCanAccess } from 'hooks'
import { useReferrerState } from 'hooks/useReferrerState'
import { ROUTES } from 'routes'
import type { SalesFlow } from 'services'
import { relativeOrDateTime } from 'utils'

import { moneyShort, PIPELINE_RANGE_OPTIONS } from '../GtmSalesPage/dealsModel'
import { SectionNote } from '../InternalAnalyticsPage/components/shared'
import {
  DEFAULT_REVENUE_PERIOD,
  inPeriod,
  masterChart,
  PERIOD_OPTIONS,
  pct,
  periodLabel,
  type MasterMeasureMode,
  type MoneyParam,
  type SourceGranularity,
  type useMasterView,
} from './masterPipelineView'

const BY_OPTIONS: readonly ViewToggleOption<SourceGranularity>[] = [
  { value: 'group', label: 'Groups' },
  { value: 'channel', label: 'Channels' },
  { value: 'sub_source', label: 'Sub-sources' },
]
const MEASURE_OPTIONS: readonly ViewToggleOption<MasterMeasureMode>[] = [
  { value: 'count', label: 'Count' },
  { value: 'revenue', label: 'Revenue' },
]
/** VRG-701 — whose source a deal is counted under: its own (Expansion included), or its company's first touch. */
const ATTRIBUTION_OPTIONS: readonly ViewToggleOption<MasterAttribution>[] = [
  { value: 'deal', label: 'Per deal' },
  { value: 'first_touch', label: 'Company lifetime' },
]

const FACET_LABELS: Record<MasterFacet, string> = {
  industry: 'Industry',
  plan: 'Plan',
  tier: 'Tier',
  country: 'Country',
  city: 'City',
  sub_source: 'Sub-source',
}


type MasterViewApi = ReturnType<typeof useMasterView>

/** `$25K` for a bound, in the period shown. */
const stopLabel = (v: number) => moneyShort(v)

/**
 * VRG-701 — a money range as a two-handle slider on the FIXED stops (`VALUE_RANGE_STOPS`, annual
 * USD, never data bounds, so a URL means the same slice next month) plus two exact fields. The
 * control shows the MRR/ARR switch's period; the URL stays canonical ANNUAL USD (VRG-700):
 * `periodBoundForInput` on the way in, `periodBoundFromInput` on the way out.
 */
const MoneyRange = ({
  label,
  api,
  min,
  max,
}: {
  label: string
  api: MasterViewApi
  min: MoneyParam
  max: MoneyParam
}) => {
  const { view, setParams } = api
  const P = periodLabel(view.period)
  const stops = useMemo(() => VALUE_RANGE_STOPS.map((s) => periodBoundForInput(s, view.period) ?? 0), [view.period])
  const shown = (k: MoneyParam) => periodBoundForInput(view.filters[k] ?? null, view.period)
  const annualParam = (v: number | null) => {
    const annual = periodBoundFromInput(v, view.period)
    return annual === null ? null : String(annual)
  }
  return (
    <RangeSlider
      label={`${label} (${P})`}
      ariaLabel={label}
      stops={stops}
      value={[shown(min), shown(max)]}
      format={stopLabel}
      prefix="$"
      // Both bounds in one URL update.
      onChange={([lo, hi]) => setParams({ [min]: annualParam(lo), [max]: annualParam(hi) })}
    />
  )
}

/**
 * VRG-701 (Spencer, 2026-10-14) — GTM → Channels' filter bar, at the PAGE level: the deal
 * pipeline chart, its tiles and the Channels table below all read the same slice, so the filters
 * sit above all three and say so. Date range first; then the facets and the two value ranges
 * (collapsible); the switches that apply to both the chart and the table (MRR/ARR, attribution)
 * on the right. Count/Revenue and Groups/Channels shape only the chart, so they sit on it.
 */
export const ChannelsFilterBar = ({ data, api }: { data: SalesFlow | undefined; api: MasterViewApi }) => {
  const { view, setParam, setFacet, clearFilters } = api
  const [expanded, setExpanded] = useState(view.activeFilters > 0)
  const optionsFor = (f: MasterFacet) => {
    const opts = (data?.facets[f] ?? []).map((o) => ({ value: o.value, label: `${o.label} (${o.n})` }))
    // A value in the URL that no deal has any more still shows, so it can be cleared.
    for (const v of view.filters[f] ?? []) if (!opts.some((o) => o.value === v)) opts.push({ value: v, label: `${v} (0)` })
    return opts
  }
  return (
    <Card>
      {/* useFlexGap: a plain Stack zeroes its children's margins, which strips the filter Grid's
          negative gutters and indents it one gutter (the VRG-736 tile-row bug). */}
      <Stack spacing={4} useFlexGap data-testid="channels-filter-bar">
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ lg: 'center' }} useFlexGap>
          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap rowGap={2}>
            <ViewToggle options={PIPELINE_RANGE_OPTIONS} value={view.preset} onChange={(v) => setParam('range', v === 'all' ? null : v)} ariaLabel="Date range" />
            <Button
              variant="secondary"
              startIcon={<SlidersHorizontalIcon strokeWidth={1} />}
              endIcon={expanded ? <ChevronUpIcon strokeWidth={1} /> : <ChevronDownIcon strokeWidth={1} />}
              onClick={() => setExpanded((x) => !x)}
              aria-expanded={expanded}
              aria-controls="channels-filters"
            >
              {view.activeFilters ? `Filters (${view.activeFilters})` : 'Filters'}
            </Button>
            {view.activeFilters > 0 && (
              <Button variant="secondary" endIcon={<XIcon strokeWidth={1} />} onClick={clearFilters}>
                Clear {view.activeFilters === 1 ? 'filter' : `${view.activeFilters} filters`}
              </Button>
            )}
          </Stack>
          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap rowGap={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ViewToggle
                options={ATTRIBUTION_OPTIONS}
                value={view.attribution}
                onChange={(v) => setParam('attribution', v === 'deal' ? null : v)}
                ariaLabel="Credit deals to"
              />
              <InfoTooltip term="sales-expansion" />
            </Stack>
            <ViewToggle options={PERIOD_OPTIONS} value={view.period} onChange={(v) => setParam('period', v === DEFAULT_REVENUE_PERIOD ? null : v)} ariaLabel="Revenue period" />
          </Stack>
        </Stack>
        <Typography variant="body3" color="text.secondary">
          These filters apply to everything below: the tiles, the deal pipeline chart and the Channels table.
        </Typography>
        </Stack>
        {/* Collapse, not `expanded && …`: a mount/unmount made the panel FLASH in at full height
            (Spencer, 2026-10-29). Plain `<Collapse>` with MUI's default timing, matching
            ScanSourcePicker's reader-help panel — the house precedent for a vertical reveal.

            The 16px gap lives INSIDE the collapsing region (the Box's `pt`) rather than coming
            from the parent Stack's `spacing`. That is also why the Collapse sits OUTSIDE that
            Stack: the Stack uses `useFlexGap`, so its gap is `gap` and cannot be overridden by a
            child's margin — left inside, a shut panel would hold 16px of dead space under the
            description and would animate from 16px rather than from 0.

            `id` moved from the Grid to the Collapse so `aria-controls` points at an element that
            always exists; while collapsed it pointed at nothing. */}
        <Collapse in={expanded} id="channels-filters">
          <Box sx={{ pt: 4 }}>
          <Grid container spacing={4}>
            {MASTER_FACETS.map((f) => (
              <Grid item key={f} xs={12} sm={6} md={4} data-testid={`master-filter-${f}`}>
                <SelectField
                  label={FACET_LABELS[f]}
                  placeholder="All"
                  multiple
                  search={(data?.facets[f].length ?? 0) > 6}
                  options={optionsFor(f)}
                  value={[...(view.filters[f] ?? [])]}
                  onChange={(v) => setFacet(f, Array.isArray(v) ? v : [v])}
                />
              </Grid>
            ))}
            <Grid item xs={12} md={6} data-testid="master-filter-value">
              <MoneyRange label="Deal value" api={api} min="value_min" max="value_max" />
            </Grid>
            <Grid item xs={12} md={6} data-testid="master-filter-actual">
              <MoneyRange label="Stripe revenue" api={api} min="actual_min" max="actual_max" />
            </Grid>
          </Grid>
          </Box>
        </Collapse>
    </Card>
  )
}

export const MasterPipelineSkeleton = () => (
  <Stack spacing={4} useFlexGap>
    <KpiTileGrid>
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} variant="rounded" height={124} sx={{ borderRadius: 3 }} />
      ))}
    </KpiTileGrid>
    <Card>
      <Skeleton variant="rounded" height={420} />
    </Card>
  </Stack>
)

/**
 * GTM → Channels' deal pipeline (VRG-696; VRG-701 made the unit the DEAL and moved the filters to
 * the page): every deal from its source, through how it entered, to the furthest stage it
 * reached and its outcome. Reads the shared flow query (`useSalesFlow`, the same one the Sales
 * flow chart reads) and owns no arithmetic.
 */
export const MasterPipelineCard = ({
  data,
  error,
  isFetching,
  api,
}: {
  data: SalesFlow | undefined
  error: unknown
  isFetching: boolean
  api: MasterViewApi
}) => {
  const theme = useTheme()
  const { view, setParam } = api
  const referrer = useReferrerState('Channels')
  const { isHasAccess: canSeeWebsite } = useCanAccess({ action: 'internal_analytics_view' })

  const flow = data
    ? view.by === 'channel'
      ? data.master.by_channel
      : view.by === 'sub_source'
        ? data.master.by_sub_source
        : data.master.by_group
    : null
  const chart = useMemo(
    () => (flow && data ? masterChart(flow, data.master.total, view.measure, view.period) : null),
    [flow, data, view.measure, view.period],
  )

  if (!data) {
    if (!error) return <MasterPipelineSkeleton />
    const status = (error as Error & { status?: number }).status
    return (
      <Card>
        <EmptyState
          icon={<LockIcon size={28} strokeWidth={1} color={theme.palette.text.secondary} />}
          title={status === 403 ? 'You cannot view the pipeline' : 'Could not load the pipeline'}
          text={
            status === 403
              ? 'The pipeline needs the "View GTM" permission and a Vera staff role.'
              : 'The request failed. Reload the page, or check the API is running.'
          }
          sx={{ py: 10 }}
        />
      </Card>
    )
  }

  const m = data.master
  const P = periodLabel(view.period)
  const open = m.by_group.nodes.find((n) => n.id === 'outcome:open')
  const won = m.by_group.nodes.find((n) => n.id === 'outcome:won')
  const lost = m.by_group.nodes.find((n) => n.id === 'outcome:lost')
  const closed = (won?.n ?? 0) + (lost?.n ?? 0)
  const wonActual = m.channels.reduce((t, r) => t + r.won_arr, 0)
  const pipelineValue = m.channels.reduce((t, r) => t + r.pipeline_arr, 0)
  // VRG-736 — the same open deals' tag margin, a secondary "+ tags" figure: the tile stays ARR.
  const pipelineTags = m.channels.reduce((t, r) => t + (r.pipeline_tag_margin ?? 0), 0)
  const small = (n: number) => n < SMALL_SAMPLE_N
  const unit = (n: number, one: string, many: string) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`

  const tiles = [
    {
      key: 'deals',
      // VRG-701 — the unit is the deal (a company can have several): new business and expansion.
      title: 'Deals',
      term: 'deal-pipeline',
      stat: m.total.n.toLocaleString('en-US'),
      unit: `${m.new_n.toLocaleString('en-US')} new · ${m.expansion_n.toLocaleString('en-US')} expansion`,
    },
    {
      key: 'pipeline',
      title: `Pipeline value, ${P}`,
      term: 'pipeline-value',
      stat: moneyShort(inPeriod(pipelineValue, view.period)),
      unit: `${unit(open?.n ?? 0, 'open deal', 'open deals')}${pipelineTags ? ` · + ${moneyShort(inPeriod(pipelineTags, view.period))} tags` : ''}`,
    },
    {
      key: 'won',
      title: `Won ${P}`,
      term: 'won-revenue',
      stat: moneyShort(inPeriod(wonActual, view.period)),
      unit: `${unit(won?.n ?? 0, 'won deal', 'won deals')}, paying in Stripe`,
    },
    {
      key: 'win-rate',
      title: 'Win rate',
      term: 'win-rate',
      stat: closed ? pct(won?.n ?? 0, closed) : '—',
      unit: closed ? `n = ${closed} closed${small(closed) ? ', small sample' : ''}` : 'none closed yet',
    },
  ]

  return (
    <Stack spacing={4} useFlexGap data-testid="master-pipeline">
      <KpiTileGrid data-testid="master-kpi-tiles" sx={{ opacity: isFetching ? 0.6 : 1, transition: 'opacity 120ms' }}>
        {tiles.map((t) => (
          <Box key={t.key} data-testid={`master-kpi-${t.key}`} sx={{ minWidth: 0 }}>
            <AnalyticsBlock title={t.title} term={t.term} stat={t.stat} unit={t.unit} statSize={28} />
          </Box>
        ))}
      </KpiTileGrid>

      <Card>
        <Stack spacing={4} component="section" aria-label="Deal pipeline: deals by source, first touch, through entry and furthest stage to outcome">
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ md: 'center' }} useFlexGap>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {/* VRG-701 — the chart's caption replaces the old card title (Spencer). */}
              <Typography variant="body3" sx={{ fontWeight: 500 }} data-testid="deal-pipeline-caption">
                Deals by source · {view.attribution === 'first_touch' ? 'company first touch' : 'first-touch'}
              </Typography>
              <InfoTooltip term="first-touch-attribution" />
              {canSeeWebsite && (
                <Typography variant="body3" color="text.secondary">
                  · Visits before it:{' '}
                  <Link component={RouterLink} to={ROUTES.INTERNAL_ANALYTICS_WEBSITE} state={referrer} underline="hover">
                    website funnel
                  </Link>
                </Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap rowGap={2}>
              <ViewToggle options={BY_OPTIONS} value={view.by} onChange={(v) => setParam('by', v === 'group' ? null : v)} ariaLabel="Sources as" />
              <ViewToggle options={MEASURE_OPTIONS} value={view.measure} onChange={(v) => setParam('measure', v === 'count' ? null : v)} ariaLabel="Weigh the flow by" />
            </Stack>
          </Stack>

          <Box sx={{ opacity: isFetching ? 0.6 : 1, transition: 'opacity 120ms' }}>
            {chart && (
              <SankeyChart
                nodes={chart.nodes}
                links={chart.links}
                height={view.by === 'group' ? 400 : 460}
                labelGutter={196}
                unit={view.measure === 'count' ? 'deals' : P}
                formatValue={view.measure === 'count' ? (v) => v.toLocaleString('en-US') : (v) => moneyShort(v)}
                nUnit="deals"
                smallSampleBelow={SMALL_SAMPLE_N}
                preserveOrder
                ariaLabel="Deals flowing from their source, through how they entered and the furthest stage they reached, to Won, Open or Lost"
              />
            )}
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <SectionNote>
              {unit(m.total.n, 'deal', 'deals')} {view.preset === 'all' ? 'at any time' : 'in this range'}
              {view.activeFilters ? ' matching the filters' : ''}.{' '}
              {m.total.without_value > 0 ? `${unit(m.total.without_value, 'deal', 'deals')} without a value (0 wide in Revenue). ` : ''}
              {m.archived_excluded > 0 ? `${unit(m.archived_excluded, 'archived deal', 'archived deals')} not shown. ` : ''}
              {view.attribution === 'first_touch'
                ? 'Company lifetime: every deal, upsells included, is credited to the channel that first brought its company in. '
                : 'Per deal: a later sale to an existing client is Expansion, so it never credits the original channel twice. '}
              Revenue: the Stripe actual if the deal has a paying subscription, at any stage, otherwise the anticipated value. Greyed shares rest on fewer than {SMALL_SAMPLE_N} deals.
              {data.leads_without_company > 0 ? ` ${unit(data.leads_without_company, 'person', 'people')} not on a deal.` : ''}{' '}
              Computed {relativeOrDateTime(data.computed_at)}.
            </SectionNote>
            <InfoTooltip term="small-sample" />
          </Stack>
        </Stack>
      </Card>
    </Stack>
  )
}
